import { useEffect, useRef, useState } from 'react';

import { useAgentPreferencesContext } from 'lib/common/contexts/AgentPreferenceContext/Context';
import SoftphoneModal from 'lib/common/contexts/SoftphoneModalContext/components/SoftphoneModal';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import Modal from 'lib/common/components/Modal';
import { TEditorRef } from 'lib/common/components/molecules/RichTextEditor/Editor';

import toast from 'lib/common/utils/toast';

import EmailSignatureEditor from './EmailSignatureEditor';
import ViewEmailSignatureModal from './ViewEmailSignatureModal';

import styles from './email-signature-modal.module.scss';

// 350 KB (DynamoDB has a limit of 400 KB)
const SIGNATURE_MAX_SIZE_BYTES = 358400;

interface IEmailSignatureModal {
  open: boolean;
  closeModal: () => void;
}

const EmailSignatureModal = ({ open, closeModal }: IEmailSignatureModal) => {
  const [editMode, setEditMode] = useState(false);
  const {
    state: { agentPreference },
    actions: { updatePreference },
  } = useAgentPreferencesContext();
  const { isSoftphone } = useLayout();
  const [emailSignature, setEmailSignature] = useState(agentPreference?.emailSignature || '');
  const signatureEditor = useRef<TEditorRef>(null);

  const titleWord = !editMode ? 'Manage' : agentPreference?.emailSignature ? 'Edit' : 'Create';

  useEffect(() => {
    setEmailSignature(agentPreference?.emailSignature || '');
    setEditMode(false);
  }, [agentPreference]);

  const closeAndClear = () => {
    setEmailSignature(agentPreference?.emailSignature || '');
    closeModal();
    setEditMode(false);
  };

  const clearAndBack = () => {
    setEmailSignature(agentPreference?.emailSignature || '');
    setEditMode(false);
  };

  const onSave = async () => {
    const htmlContent = await signatureEditor?.current?.getHTMLContent();
    const byteLength = new TextEncoder().encode(htmlContent).byteLength;

    if (byteLength > SIGNATURE_MAX_SIZE_BYTES) {
      toast('info', 'Your email signature exceeds the 350 KB size limit. Try reducing image sizes and try again.');

      return Promise.reject();
    }

    await updatePreference({ emailSignature: htmlContent });
  };

  const modalContent = editMode ? (
    <EmailSignatureEditor
      setEditMode={setEditMode}
      emailSignature={emailSignature}
      setEmailSignature={setEmailSignature}
      ref={signatureEditor}
    />
  ) : (
    <ViewEmailSignatureModal
      emailSignatureInsertOn={agentPreference?.emailSignatureInsetOn!}
      setEditMode={setEditMode}
      emailSignature={emailSignature}
      updatePreference={updatePreference}
    />
  );

  const softphoneButtons = editMode
    ? {
        onCancel: clearAndBack,
        onSave,
        onSuccess: () => {
          setEditMode(false);
        },
        secondaryButtonText: 'Back',
      }
    : {};

  return isSoftphone ? (
    <SoftphoneModal
      title={`${titleWord} Email Signature`}
      isModalOpen={open}
      handleClearContent={() => {}}
      isReversedLayout={false}
      {...softphoneButtons}
      closeModal={closeAndClear}
    >
      <div className={styles['email-signature-modal']}>{modalContent}</div>
    </SoftphoneModal>
  ) : (
    <Modal
      large
      open={open}
      onClose={closeAndClear}
      onSecondary={clearAndBack}
      secondaryButtonText="Back"
      hideFooter={!editMode}
      onSave={onSave}
      onSuccess={() => {
        setEditMode(false);
      }}
      title={`${titleWord} Email Signature`}
    >
      <div className={styles['email-signature-modal']}>{modalContent}</div>
    </Modal>
  );
};

export default EmailSignatureModal;
